import { useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Loader2, Play, PlugZap, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, PageState, UpgradeGate } from "@/components/app/Common";
import { SimpleSelect } from "@/components/app/SimpleSelect";
import { NOT_CONFIGURED, ConfidenceBadge, Sources, resetResearchStatus } from "@/components/app/Research";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";

const STATE = { ok: ["API key connected", "text-emerald-600 dark:text-emerald-400"], missing: [NOT_CONFIGURED, "text-muted-foreground"], invalid: ["The saved key was rejected. Add a valid Perplexity key.", "text-red-500"] };
const money = (n) => `$${(n || 0).toFixed(n < 1 ? 4 : 2)}`;

const Result = ({ r }) => (
  <div className="mt-3 rounded-lg border border-border p-3 text-sm" data-testid={`lab-result-${r.feature}`}>
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"><ConfidenceBadge c={r.confidence} testId={`lab-confidence-${r.feature}`} />
      <span data-testid={`lab-ms-${r.feature}`}>{(r.ms / 1000).toFixed(1)}s</span><span>· {r.tokens.in + r.tokens.out} tokens</span><span data-testid={`lab-cost-${r.feature}`}>· {money(r.cost)} {r.cost_basis}</span><span>· {r.sources.length} sources</span></div>
    <p className="mt-2 whitespace-pre-line">{r.answer}</p>
    <Sources sources={r.sources} testId={`lab-sources-${r.feature}`} />
  </div>
);

const Tester = ({ f, label, sample, ready, on, usage, onToggle }) => {
  const [q, setQ] = useState("");
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      setR((await api.post("/research/lab/run", { feature: f, query: q })).data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || errMsg(e));
    }
    setBusy(false);
  };
  const validated = (usage?.tests || 0) > 0;
  return (
    <div className="surface p-5" data-testid={`lab-card-${f}`}>
      <div className="flex flex-wrap items-center gap-3"><b className="text-sm flex-1">{label}</b>
        {validated && <span className="text-[11px] text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Tested</span>}
        <span className="text-xs text-muted-foreground">{on ? "On for everyone" : "Off"}</span><Switch checked={on} onCheckedChange={onToggle} data-testid={`lab-toggle-${f}`} /></div>
      {usage && <div className="text-[11px] text-muted-foreground mt-1" data-testid={`lab-usage-${f}`}>30 days: {usage.calls} calls ({usage.tests} tests) · avg {(usage.avg_ms / 1000).toFixed(1)}s · {money(usage.cost)} · confidence {usage.high} high / {usage.medium} medium / {usage.low} low</div>}
      <Textarea rows={2} className="mt-3 text-sm" placeholder={sample} value={q} onChange={(e) => setQ(e.target.value)} data-testid={`lab-query-${f}`} />
      <Button size="sm" className="mt-2" onClick={run} disabled={busy || !ready} data-testid={`lab-run-${f}`}>{busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}Run live test</Button>
      {r && <Result r={r} />}
    </div>
  );
};

const Thresholds = ({ d, save }) => (
  <div className="surface p-5 space-y-3" data-testid="lab-thresholds">
    <b className="text-sm">Confidence thresholds</b>
    <div className="text-xs space-y-1.5 text-muted-foreground">
      <p><b className="text-foreground">High:</b> government, regulator or official sources, or several independent sources agreeing.</p>
      <p><b className="text-foreground">Medium:</b> industry publications or trade associations, or partly corroborated findings.</p>
      <p><b className="text-foreground">Low:</b> one source, unverified reports or forums. Informational only and never triggers actions.</p>
    </div>
    <div><div className="text-xs mb-1">Minimum confidence for automatic recommendations (Attention Board, Monday briefing)</div>
      <SimpleSelect value={d.min_confidence} onChange={(v) => save({ min_confidence: v })} options={[{ value: "medium", label: "Medium and above" }, { value: "high", label: "High only" }]} testId="lab-min-confidence" /></div>
    <p className="text-xs" data-testid="lab-critical-rule"><b>Compliance-critical changes</b> (filing deadlines, entity records) always need <b>high</b> confidence plus your explicit confirmation, and every change is saved in the audit trail.</p>
  </div>
);

export default function ResearchLab() {
  const state = useLoad(() => api.get("/research/lab").then((r) => r.data));
  if (state.gated) return <div><PageHeader eyebrow="External intelligence" title="Research validation" /><UpgradeGate gated={state.gated} /></div>;
  const d = state.data;
  const save = async (patch) => {
    try {
      await api.put("/research/settings", { min_confidence: d.min_confidence, features: d.features, ...patch });
      resetResearchStatus();
      toast.success("Research settings saved");
      state.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="External intelligence" title="Research validation" subtitle="Run live Perplexity tests, review source quality, speed and estimated cost, then turn each research feature on when you trust it." />
      <PageState state={state}>
        {d && <div className="grid lg:grid-cols-[1fr_340px] gap-6">
          <div className="space-y-4 order-2 lg:order-1">{Object.entries(d.labels).map(([f, label]) => (
            <Tester key={f} f={f} label={label} sample={d.samples[f]} ready={d.state === "ok"} on={d.features[f]} usage={d.usage[f]}
              onToggle={(v) => save({ features: { ...d.features, [f]: v } })} />))}</div>
          <div className="space-y-4 order-1 lg:order-2">
            <div className="surface p-5" data-testid="lab-connection"><div className="flex items-center gap-2"><FlaskConical className="h-4 w-4 text-primary" /><b className="text-sm">Perplexity connection</b></div>
              <p className={`text-xs mt-2 flex gap-1.5 ${STATE[d.state][1]}`} data-testid={`lab-state-${d.state}`}><PlugZap className="h-3.5 w-3.5 shrink-0" />{STATE[d.state][0]}</p>
              {d.state !== "ok" && <p className="text-[11px] text-muted-foreground mt-1">Create a key at console.perplexity.ai, then ask your builder to connect it.</p>}
              <div className="text-xs mt-3" data-testid="lab-total-cost">Estimated spend, last 30 days: <b>{money(d.total_cost_30d)}</b></div></div>
            <Thresholds d={d} save={save} />
            {d.recent.length > 0 && <div className="surface p-5 text-xs" data-testid="lab-recent"><b className="text-sm">Recent calls</b>
              <ul className="mt-2 space-y-1">{d.recent.map((r, i) => <li key={`${r.at}-${i}`} className="flex flex-wrap justify-between gap-x-2"><span>{d.labels[r.feature]}{r.test && " (test)"} · {fmtDate(r.at)}</span><span className="font-mono text-[11px] whitespace-nowrap">{(r.ms / 1000).toFixed(1)}s · {money(r.cost)} · {r.confidence}</span></li>)}</ul></div>}
          </div>
        </div>}
      </PageState>
    </div>
  );
}
